import { useLanguage, useMessageActionContext } from '@/app/providers';
import { StarIcon } from '@/assets/images';
import { Checkbox, CustomButton } from '@/components';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { useMessageFormatter } from '@/features/chats/viewmodels/useMessageFormatter';
import { useMessageTruncation } from '@/features/chats/viewmodels/useMessageTruncation';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId } from '@/shared/utils';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import PropTypes from 'prop-types';
import React, { memo, useMemo } from 'react';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';

const SENDER_CLASS = 'sender-chat chat-bubble text-bubble ct-flex ct-rowReverse ct-aiEnd ct-gap10 ct-padding-lr8-rl16';
const RECEIVER_CLASS = 'receiver-chat chat-bubble text-bubble ct-flex ct-gap10 ct-padding-lr8-rl16';

const renderLines = (content, msgId, prefix = '') =>
   content.split('\n').map((line, i, arr) => (
      <React.Fragment key={`${msgId}-${prefix}line-${i}`}>
         {line}
         {i < arr.length - 1 && <br />}
      </React.Fragment>
   ));

const renderFormattedContent = (formattedContent, msgId) => {
   if (Array.isArray(formattedContent)) {
      return formattedContent.map((part, index) =>
         typeof part === 'string'
            ? renderLines(part, msgId, `part-${index}-`)
            : React.cloneElement(part, { key: part.key || `${msgId}-part-${index}` }),
      );
   }
   if (typeof formattedContent === 'string') return renderLines(formattedContent, msgId);
   return formattedContent;
};

const TextCard = memo(({ message, activeChatJid }) => {
   const { msgId, msgBody = {}, timestamp: msgTimestamp, isHighlighted, msgStatus, editedStatus } = message;

   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);
   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();
   const { t } = useLanguage();
   const { hideAvatar = false, hideActions = false, timeFormat = 'message-card' } = useMessageActionContext();

   const { rawContent, mentionedUsersIds, timestamp } = useMemo(
      () => ({
         rawContent: msgBody?.message,
         mentionedUsersIds: msgBody?.mentionedUsersIds ?? [],
         timestamp: msgTimestamp ? formatChatTime(new Date(Number(msgTimestamp)), timeFormat) : '',
      }),
      [msgBody, msgTimestamp, timeFormat],
   );

   const { isTruncated, isExpanded, displayText, toggleExpand } = useMessageTruncation(rawContent);
   const formattedContent = useMessageFormatter(displayText, { mentionedUsersIds, isClickable: true });
   const renderedContent = renderFormattedContent(formattedContent, msgId);
   const hideAvatarTextCard = isSender || hideAvatar;
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const showProfile = isChatGroup && !hideAvatarTextCard;

   return (
      <CustomButton
         as="div"
         className={`message-row custom-message-card ${isSelectionActive && !hideActions ? 'cts-text ct-flex ct-aiCenter selection-active' : ''} ${isSelected && !hideActions ? 'selected' : ''} ${isHighlighted && !hideActions ? 'search-highlight-active' : ''}`}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-text">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}
         <div className={`${isSender ? SENDER_CLASS : RECEIVER_CLASS}`}>
            {showProfile && (
               <picture className="chat-avatar cts-text">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}
            <div className={`ct-flex ct-relative ${isSender ? 'ct-flexColEnd' : 'ct-flexCol'} ct-gap4`}>
               <div className={`chat-box ct-flex ct-flexCol ct-gap10 ${msgBody?.replyTo ? 'reply-bubble' : ''}`}>
                  {showProfile && <Username className="cts-text ct-username" userId={senderId} />}
                  <ReplyMessageCard replyTo={msgBody?.replyTo} activeChatJid={activeChatJid} />
                  <p>
                     {renderedContent}
                     <br />
                     {isTruncated && (
                        <button className="view-more-less-btn" onClick={toggleExpand}>
                           {isExpanded ? t('message.VIEW_LESS') : t('message.VIEW_MORE')}
                        </button>
                     )}
                  </p>
               </div>
               <div className={`cts-text ct-flexAlign ct-flex ct-gap8 ${isSender ? 'ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={msgStatus} />}
                  <span className="time cts-text">{timestamp}</span>
                  {editedStatus === 1 && <span className="edited-icon">{t('message.EDITED')}</span>}
                  {message.favouriteStatus === 1 && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>
               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-text"
                     message={message}
                     isUser={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

TextCard.displayName = 'TextCard';

TextCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};

export default TextCard;
